import React, {useEffect} from 'react';
import {Box, Card, CardContent, CardHeader, List, ListItem, ListItemAvatar, Skeleton, Typography,} from '@mui/material';
import ContextViewFileList from './Contexts/ContextViewFileList';
import {ViewFileListPath} from './ViewFileListPath';
import {EmptyDirectoryMessage, FileDropZone, FileListItem, NavigationBar} from './Components';
import {DirView} from '../services/openapi';
import {FileWithPath} from 'react-dropzone';

// Custom Style-Komponente für den blauen Akzent
const BlueAccent = () => <Box sx={{width: 48, height: 4, bgcolor: '#1976d2', borderRadius: 2}}/>;

export const FileListDetails = () => {
    const {
        dir_id_history,
        curr_dir_id,
        curr_dir_view,
        accessible_elements,
        user_specific,
        loading_dir,
        dir_navigate_back,
        dir_navigate_forward,
        update_dir_view,
    } = React.useContext(ContextViewFileList);
    const [deletedFiles, setDeletedFiles] = React.useState<Set<string>>(new Set());
    const [movedFiles, setMovedFiles] = React.useState<Set<string>>(new Set());
    const [uploadedFiles, setUploadedFiles] = React.useState<Map<string, FileWithPath>>(new Map());
    const [parentFolder, setParentFolder] = React.useState<DirView>({
        parent: '',
        hidrive_id: '',
        ending: '',
        type: 'system',
        isUnread: false,
    });

    useEffect(() => {
        if (dir_id_history && dir_id_history.length > 1) {
            setParentFolder({
                parent: dir_id_history[0].name,
                hidrive_id: '',
                ending: '',
                type: 'system',
                isUnread: false,
            });
        }
    }, [dir_id_history]);

    const handle_dir_click = (id: string) => {
        dir_navigate_forward(id);
    };

    const handleDeleteStart = (fileId: string) => {
        setDeletedFiles((prev) => new Set([...prev, fileId]));
    };

    const handleDeleteSuccess = (fileId: string) => {
        // Keep the file in deletedFiles set since deletion was successful
    };

    const handleDeleteError = (fileId: string) => {
        setDeletedFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(fileId);
            return newSet;
        });
    };

    const handleMoveStart = (fileId: string) => {
        setMovedFiles((prev) => new Set([...prev, fileId]));
    };

    const handleMoveSuccess = (fileId: string) => {
        // Keep the file in movedFiles set since move was successful
    };

    const handleMoveError = (fileId: string) => {
        setMovedFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(fileId);
            return newSet;
        });
    };

    const handleUploadStart = (files: FileWithPath[]) => {
        // Create temporary IDs for the files being uploaded
        files.forEach((file) => {
            const tempId = `temp_${Date.now()}_${file.name}`;
            setUploadedFiles((prev) => new Map(prev).set(tempId, file));
        });
    };

    const handleUploadSuccess = (files: FileWithPath[]) => {
        // Remove the temporary files from the uploadedFiles map
        files.forEach((file) => {
            setUploadedFiles((prev) => {
                const newMap = new Map(prev);
                // Find and remove the file with matching name
                for (const [id, f] of newMap.entries()) {
                    if (f.name === file.name) {
                        newMap.delete(id);
                        break;
                    }
                }
                return newMap;
            });
        });
        
        // Refresh the directory view to show the newly uploaded files
        update_dir_view(curr_dir_id);
    };

    const handleUploadError = (files: FileWithPath[]) => {
        // Remove the temporary files from the uploadedFiles map
        files.forEach((file) => {
            setUploadedFiles((prev) => {
                const newMap = new Map(prev);
                // Find and remove the file with matching name
                for (const [id, f] of newMap.entries()) {
                    if (f.name === file.name) {
                        newMap.delete(id);
                        break;
                    }
                }
                return newMap;
            });
        });
    };

    const hasChildren =
        'children' in curr_dir_view &&
        Array.isArray(curr_dir_view.children) &&
        curr_dir_view.children.length > 0;

    // Create temporary file entries for uploaded files
    const uploadedFileEntries = Array.from(uploadedFiles.entries()).map(([id, file]) => ({
        hidrive_id: id,
        parent: file.name,
        type: 'file',
        isUnread: false,
    }));

    const sortedChildren =
        hasChildren && 'children' in curr_dir_view && Array.isArray(curr_dir_view.children)
            ? [...curr_dir_view.children, ...uploadedFileEntries]
                .filter((child) => !deletedFiles.has(child.hidrive_id) && !movedFiles.has(child.hidrive_id))
                .sort((a, b) =>
                    a.type === b.type ? (a.parent >= b.parent ? 1 : -1) : a.type === 'file' ? 1 : -1
                )
            : [];

    // Check if current directory is an Upload folder
    const isUploadFolder = curr_dir_view && 'parent' in curr_dir_view &&
        (curr_dir_view.parent.toLowerCase().includes('upload') ||
            curr_dir_view.parent.toLowerCase() === 'upload');

    // Custom Header-Komponente, die Titel und blauen Akzent enthält
    const customTitle = user_specific && (
        <Box sx={{textAlign: 'left'}}>
            <Typography
                variant='h4'
                sx={{
                    fontWeight: 'bold',
                }}>
                Meine Dokumente
            </Typography>
            <BlueAccent/>
        </Box>
    );

    // Add skeleton items for loading state
    const renderSkeletonItems = () => {
        return Array(4)
            .fill(0)
            .map((_, index) => (
                <ListItem
                    key={`skeleton-${index}`}
                    sx={{
                        minHeight: '64px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                    }}>
                    <ListItemAvatar sx={{position: 'relative'}}>
                        <Skeleton variant='circular' width={40} height={40}/>
                    </ListItemAvatar>
                    <div style={{flexGrow: 1, overflow: 'hidden'}}>
                        <Skeleton variant='text' width='60%' height={24}/>
                    </div>
                </ListItem>
            ));
    };

    return (
        <Card
            sx={{
                p: {xs: 0, sm: 1, md: 2}, // Responsive padding: kleiner auf Mobile, größer auf Desktop
            }}>
            {user_specific && <CardHeader title={customTitle} disableTypography sx={{pb: 0}}/>}
            <CardContent sx={{pt: user_specific ? 1 : 2}}>
                <ViewFileListPath/>
                <NavigationBar
                    dirIdHistory={dir_id_history}
                    currDirId={curr_dir_id}
                    currDirView={curr_dir_view}
                    userSpecific={user_specific || false}
                    dirNavigateBack={() => dir_navigate_back()}
                />
                {loading_dir && renderSkeletonItems()}
                {!loading_dir && !hasChildren && <EmptyDirectoryMessage loadingDir={loading_dir}/>}
                {!loading_dir && isUploadFolder && (
                    <>
                        <p style={{marginLeft: 16, color: '#444' }}>Laden Sie hier Dateien hoch die Sie uns gerne zukommen lassen möchten. <br/> Bspw. Vollmachten, Rechnungen, etc. </p>
                        <FileDropZone
                            dir_id={curr_dir_id!}
                            onUploadStart={handleUploadStart}
                            onUploadSuccess={handleUploadSuccess}
                            onUploadError={handleUploadError}
                        />
                    </>
                )}
                {!loading_dir && hasChildren && (
                    <List>
                        {sortedChildren.map((element) => (
                            <FileListItem
                                key={element.hidrive_id}
                                element={element}
                                accessibleElements={accessible_elements}
                                userSpecific={user_specific}
                                onDirectoryClick={handle_dir_click}
                                setLoading={() => {
                                }}
                                onDeleteStart={() => handleDeleteStart(element.hidrive_id)}
                                onDeleteSuccess={() => handleDeleteSuccess(element.hidrive_id)}
                                onDeleteError={() => handleDeleteError(element.hidrive_id)}
                                onMoveStart={() => handleMoveStart(element.hidrive_id)}
                                onMoveSuccess={() => handleMoveSuccess(element.hidrive_id)}
                                onMoveError={() => handleMoveError(element.hidrive_id)}
                                onUploadStart={handleUploadStart}
                                onUploadSuccess={handleUploadSuccess}
                                onUploadError={handleUploadError}
                            />
                        ))}
                    </List>
                )}
            </CardContent>
        </Card>
    );
};
